import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DataGridColumn, BuilderNode } from '../../interfaces/builder-screen.models';

interface ColumnPreview {
  field: string;
  header: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'search' | 'dropdown';
  dataPath: string;
  selected: boolean;
  sampleValue?: any;
  // Propriedades inferidas do formulário
  searchProperties?: { entityName: string; fields: { keyField: string; valueField: string } };
  showKey?: boolean;
  options?: Array<{ value: any; label: string; isDefault?: boolean }>;
}

@Component({
  selector: 'app-smart-column-builder',
  templateUrl: './smart-column-builder.component.html',
  styleUrls: ['./smart-column-builder.component.scss'],
  standalone: false
})
export class SmartColumnBuilderComponent {
  @Input() visible = false;
  @Input() currentColumns: DataGridColumn[] = [];
  @Input() formNodes: BuilderNode[] = [];
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() columnsGenerated = new EventEmitter<{ columns: DataGridColumn[], dataPath: string }>();

  // Estado
  jsonInput = '';
  jsonValid = false;
  detectedColumns: ColumnPreview[] = [];
  dataPathPrefix = ''; // ✅ VAZIO por padrão - auto-detectar
  step: 'input' | 'preview' = 'input';
  errorMessage = '';

  // Templates prontos
  templates = [
    {
      name: 'E-commerce Produtos',
      json: JSON.stringify({
        products: [
          { id: 1, name: 'Notebook', category: 'Eletrônicos', price: 3500, stock: 15, active: true, createdAt: '2025-01-15' }
        ]
      }, null, 2),
      dataPath: 'products'
    },
    {
      name: 'Clientes',
      json: JSON.stringify({
        customers: [
          { id: 1, name: 'João Silva', email: 'joao@example.com', phone: '(11) 98765-4321', city: 'São Paulo', active: true }
        ]
      }, null, 2),
      dataPath: 'customers'
    },
    {
      name: 'Pedidos',
      json: JSON.stringify({
        orders: [
          { id: 1001, customerId: 1, customerName: 'João Silva', date: '2025-10-15', total: 4500, status: 'Entregue' }
        ]
      }, null, 2),
      dataPath: 'orders'
    }
  ];

  constructor() {}

  /**
   * Abre o modal
   */
  open() {
    this.visible = true;
    this.step = 'input';
    this.jsonInput = '';
    this.detectedColumns = [];
    this.errorMessage = '';
    this.dataPathPrefix = ''; // ✅ Limpar path para forçar auto-detecção
    this.visibleChange.emit(true);
  }

  /**
   * Fecha o modal
   */
  close() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  /**
   * Aplica um template pré-definido
   */
  applyTemplate(template: any) {
    console.log('📋 Aplicando template:', template.name);
    this.jsonInput = template.json;
    this.dataPathPrefix = template.dataPath;
    
    // Auto-analisar após aplicar template
    setTimeout(() => {
      this.analyzeJSON();
    }, 100);
  }

  /**
   * Analisa o JSON colado e detecta colunas
   */
  analyzeJSON() {
    try {
      console.log('=== 🚀 INICIANDO ANÁLISE DE JSON ===');
      console.log('📝 JSON Input (primeiros 200 chars):', this.jsonInput.substring(0, 200));
      console.log('📍 Data Path Prefix fornecido:', this.dataPathPrefix || '(vazio - auto-detectar)');
      
      this.errorMessage = '';
      const parsed = JSON.parse(this.jsonInput);
      
      console.log('✅ JSON parseado com sucesso!');
      console.log('📦 Tipo do objeto parseado:', typeof parsed);
      console.log('📦 É array?:', Array.isArray(parsed));
      console.log('📦 Keys do objeto:', Object.keys(parsed));
      
      // 🎯 NOVO: Auto-detectar array se Data Path estiver vazio
      let arrayData: any[] = [];
      let detectedPath = this.dataPathPrefix;
      
      if (!this.dataPathPrefix || this.dataPathPrefix.trim() === '') {
        console.log('🔍 Iniciando busca automática de arrays...');
        // Buscar primeiro array no JSON automaticamente
        const result = this.findFirstArray(parsed);
        
        console.log('🔍 Resultado da busca:', result);
        
        if (result) {
          arrayData = result.array;
          detectedPath = result.path;
          this.dataPathPrefix = detectedPath;
          console.log('✅ Array detectado automaticamente em:', detectedPath);
          console.log('✅ Array tem', arrayData.length, 'itens');
        } else {
          console.log('❌ Nenhum array foi encontrado pela busca automática');
        }
      } else {
        console.log('📍 Usando path fornecido:', this.dataPathPrefix);
        // Usar path fornecido
        arrayData = this.findArrayInObject(parsed, this.dataPathPrefix);
        console.log('📍 Array encontrado no path fornecido:', arrayData?.length || 0, 'itens');
      }
      
      if (!arrayData || arrayData.length === 0) {
        console.log('❌ ERRO: Array vazio ou não encontrado');
        console.log('❌ arrayData:', arrayData);
        this.errorMessage = `❌ Nenhum array encontrado no JSON.
        
💡 Dicas:
• Se o JSON tem arrays aninhados, deixe o "Data Path" vazio para auto-detectar
• Ou especifique o caminho, ex: "response.item_list"

🔍 Debug Info:
• Objeto parseado: ${typeof parsed}
• Keys disponíveis: ${Object.keys(parsed).join(', ')}`;
        this.jsonValid = false;
        return;
      }

      // Pegar primeiro item como exemplo
      const sampleItem = arrayData[0];
      
      // Detectar colunas automaticamente
      this.detectedColumns = this.extractColumns(sampleItem);
      
      // Enriquecer com tipos do formulário (search, dropdown)
      this.detectedColumns = this.enrichColumnsFromFormNodes(this.detectedColumns);
      
      this.jsonValid = true;
      this.step = 'preview';
      
      console.log('✅ Array encontrado:', detectedPath);
      console.log('✅ Colunas detectadas:', this.detectedColumns.length);
      
    } catch (error: any) {
      this.errorMessage = `❌ JSON inválido: ${error.message}

💡 Verifique:
• Vírgulas faltando ou sobrando
• Chaves { } e colchetes [ ] fechados corretamente
• Aspas duplas " ao redor de textos`;
      this.jsonValid = false;
      this.detectedColumns = [];
    }
  }

  /**
   * 🆕 Busca RECURSIVAMENTE o primeiro array no JSON
   * Retorna o array E o path para ele
   * Prioriza arrays com objetos (não primitivos)
   */
  private findFirstArray(obj: any, parentPath = '', depth = 0): { array: any[], path: string } | null {
    const maxDepth = 10; // Prevenir recursão infinita
    
    if (depth > maxDepth) {
      console.warn('⚠️ Max depth atingida na busca por arrays');
      return null;
    }
    
    console.log(`🔍 Buscando array em: "${parentPath}" (depth: ${depth})`);
    
    // Se já é um array, retornar
    if (Array.isArray(obj)) {
      if (obj.length > 0) {
        console.log(`✅ Array encontrado em: "${parentPath}" com ${obj.length} itens`);
        return { array: obj, path: parentPath || 'root' };
      } else {
        console.log(`⚠️ Array vazio em: "${parentPath}"`);
        return null;
      }
    }
    
    // Se não é objeto, não tem arrays aqui
    if (!obj || typeof obj !== 'object') {
      return null;
    }
    
    // Buscar em todas as propriedades
    const keys = Object.keys(obj);
    console.log(`📂 Explorando objeto com ${keys.length} propriedades:`, keys);
    
    // PRIMEIRO: Procurar arrays diretos nas propriedades
    for (const key of keys) {
      const value = obj[key];
      const currentPath = parentPath ? `${parentPath}.${key}` : key;
      
      // Se é um array com itens, retornar imediatamente
      if (Array.isArray(value) && value.length > 0) {
        // Verificar se o array contém objetos (prioritário) ou primitivos
        const firstItem = value[0];
        const isObjectArray = typeof firstItem === 'object' && firstItem !== null && !Array.isArray(firstItem);
        
        if (isObjectArray) {
          console.log(`✅ Array de objetos encontrado em: "${currentPath}" com ${value.length} itens`);
          return { array: value, path: currentPath };
        }
      }
    }
    
    // SEGUNDO: Se não encontrou arrays de objetos, aceitar qualquer array
    for (const key of keys) {
      const value = obj[key];
      const currentPath = parentPath ? `${parentPath}.${key}` : key;
      
      if (Array.isArray(value) && value.length > 0) {
        console.log(`✅ Array encontrado em: "${currentPath}" com ${value.length} itens`);
        return { array: value, path: currentPath };
      }
    }
    
    // TERCEIRO: Buscar recursivamente em sub-objetos
    for (const key of keys) {
      const value = obj[key];
      const currentPath = parentPath ? `${parentPath}.${key}` : key;
      
      // Se é objeto (não array), buscar recursivamente
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const result = this.findFirstArray(value, currentPath, depth + 1);
        if (result) {
          return result;
        }
      }
    }
    
    console.log(`❌ Nenhum array encontrado a partir de: "${parentPath}"`);
    return null;
  }

  /**
   * Encontra array no objeto usando path específico
   */
  private findArrayInObject(obj: any, path: string): any[] {
    if (!path || path.trim() === '') {
      // Se path vazio, buscar primeiro array
      const result = this.findFirstArray(obj);
      return result ? result.array : [];
    }

    // Resolver path fornecido
    const parts = path.split('.');
    let current = obj;
    
    for (const part of parts) {
      if (current && typeof current === 'object' && part in current) {
        current = current[part];
      } else {
        return [];
      }
    }
    
    return Array.isArray(current) ? current : [];
  }

  /**
   * Extrai colunas de um objeto de exemplo
   */
  private extractColumns(sampleItem: any, prefix = ''): ColumnPreview[] {
    const columns: ColumnPreview[] = [];

    for (const [key, value] of Object.entries(sampleItem)) {
      const fieldPath = prefix ? `${prefix}.${key}` : key;
      
      // Detectar tipo automaticamente
      const detectedType = this.detectType(value);
      
      // Se for objeto aninhado, adicionar como texto (pode ser expandido depois)
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Objeto aninhado - adicionar como JSON string por enquanto
        columns.push({
          field: key,
          header: this.formatHeader(key),
          type: 'text',
          dataPath: fieldPath,
          selected: false, // Não selecionar por padrão objetos aninhados
          sampleValue: JSON.stringify(value)
        });
        
        // Também adicionar campos do objeto aninhado
        const nestedColumns = this.extractColumns(value, fieldPath);
        columns.push(...nestedColumns);
      } else if (!Array.isArray(value)) {
        // Campo simples
        columns.push({
          field: key,
          header: this.formatHeader(key),
          type: detectedType,
          dataPath: fieldPath,
          selected: true, // Selecionar por padrão
          sampleValue: value
        });
      }
    }

    return columns;
  }

  /**
   * Detecta tipo do campo baseado no valor
   */
  private detectType(value: any): 'text' | 'number' | 'date' | 'boolean' {
    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'number') return 'number';
    
    // Tentar detectar data
    if (typeof value === 'string') {
      // Padrões de data comuns
      const datePatterns = [
        /^\d{4}-\d{2}-\d{2}$/,        // 2025-01-15
        /^\d{2}\/\d{2}\/\d{4}$/,      // 15/01/2025
        /^\d{4}-\d{2}-\d{2}T/         // ISO 8601
      ];
      
      for (const pattern of datePatterns) {
        if (pattern.test(value)) {
          return 'date';
        }
      }
    }
    
    return 'text';
  }

  /**
   * Formata nome do campo para header legível
   */
  private formatHeader(field: string): string {
    // Converter camelCase ou snake_case para Title Case
    return field
      .replace(/([A-Z])/g, ' $1')       // camelCase -> camel Case
      .replace(/_/g, ' ')                // snake_case -> snake case
      .replace(/\b\w/g, c => c.toUpperCase()) // Title Case
      .trim();
  }

  /**
   * Alterna seleção de uma coluna
   */
  toggleColumn(column: ColumnPreview) {
    column.selected = !column.selected;
  }

  /**
   * Seleciona/Deseleciona todas as colunas
   */
  toggleAll() {
    const allSelected = this.detectedColumns.every(c => c.selected);
    this.detectedColumns.forEach(c => c.selected = !allSelected);
  }

  /**
   * Volta para a etapa de input
   */
  goBack() {
    this.step = 'input';
  }

  /**
   * Confirma e gera colunas, enriquecendo com tipos do formulário
   */
  confirm() {
    const enriched = this.enrichColumnsFromFormNodes(this.detectedColumns);
    
    const selectedColumns = enriched
      .filter(c => c.selected)
      .map(c => {
        const col: DataGridColumn = {
          field: c.field,
          header: c.header,
          type: c.type,
          dataPath: c.dataPath,
          width: this.getDefaultWidth(c.type),
          sortable: true,
          filterable: c.type === 'text',
          editable: c.type === 'search' || c.type === 'dropdown' ? true : false
        };
        if (c.type === 'search' && c.searchProperties) {
          col.searchProperties = c.searchProperties;
          col.showKey = c.showKey;
        }
        if (c.type === 'dropdown' && c.options) {
          col.options = c.options;
        }
        return col;
      });

    this.columnsGenerated.emit({
      columns: selectedColumns,
      dataPath: this.dataPathPrefix
    });
    
    this.close();
  }

  /**
   * Cruza colunas detectadas com campos do formulário para inferir tipos ricos.
   * Se um campo search ou dropdown do formulário tem o mesmo dataPath que uma coluna,
   * o tipo da coluna é promovido para search/dropdown com as propriedades correspondentes.
   */
  private enrichColumnsFromFormNodes(columns: ColumnPreview[]): ColumnPreview[] {
    if (!this.formNodes || this.formNodes.length === 0) return columns;

    const formFieldMap = new Map<string, BuilderNode>();
    for (const node of this.formNodes) {
      const props = node.props as any;
      if (props?.dataPath) {
        formFieldMap.set(props.dataPath, node);
      }
    }

    return columns.map(col => {
      const formNode = formFieldMap.get(col.dataPath);
      if (!formNode) return col;

      const props = formNode.props as any;

      if (formNode.type === 'search' && props.searchProperties) {
        return {
          ...col,
          type: 'search' as const,
          searchProperties: props.searchProperties,
          showKey: props.showKey || false
        };
      }

      if (formNode.type === 'dropdown' && props.options && props.options.length > 0) {
        return {
          ...col,
          type: 'dropdown' as const,
          options: props.options
        };
      }

      return col;
    });
  }

  /**
   * Retorna largura padrão baseada no tipo
   */
  private getDefaultWidth(type: string): string {
    switch (type) {
      case 'number': return '100px';
      case 'boolean': return '80px';
      case 'date': return '120px';
      case 'search': return '220px';
      case 'dropdown': return '180px';
      default: return '200px';
    }
  }

  /**
   * Exporta configuração atual como JSON
   */
  exportConfig() {
    const config = {
      dataPath: this.dataPathPrefix,
      columns: this.detectedColumns.filter(c => c.selected)
    };
    
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'datagrid-config.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Retorna ícone baseado no tipo
   */
  getTypeIcon(type: string): string {
    switch (type) {
      case 'text': return 'font-size';
      case 'number': return 'number';
      case 'date': return 'calendar';
      case 'boolean': return 'check-square';
      case 'search': return 'search';
      case 'dropdown': return 'down-square';
      default: return 'field-string';
    }
  }

  getTypeColor(type: string): string {
    switch (type) {
      case 'text': return '#1890ff';
      case 'number': return '#52c41a';
      case 'date': return '#faad14';
      case 'boolean': return '#722ed1';
      case 'search': return '#eb2f96';
      case 'dropdown': return '#13c2c2';
      default: return '#8c8c8c';
    }
  }

  /**
   * Retorna quantidade de colunas selecionadas
   */
  getSelectedColumnsCount(): number {
    return this.detectedColumns.filter(c => c.selected).length;
  }
}

